import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Display, Meta, Rule, Chip } from '../components/Atoms';
import { LoadingState } from '../components/states';
import { useApi, formatDate } from '../lib/api';

/**
 * PublicProfile - widok publiczny profilu ogrodnika.
 *
 * URL: /g/:username (publiczny, bez auth)
 * Endpoint: GET /api/g/:username
 *
 * Sekcje:
 *   - Hero (cover photo, avatar, nazwa, tagline, lokalizacja, social, sezon)
 *   - Featured (roślina sezonu — manual pick)
 *   - Bio + about long
 *   - Stats (działki, rośliny, plon)
 *   - Timeline (co teraz rośnie)
 *   - Harvests (ostatnie zbiory)
 *   - Gallery (publiczne zdjęcia)
 */

const GARDEN_TYPE_LABEL = {
  vegetable: 'Warzywnik',
  flowers: 'Rabata kwiatowa',
  balcony: 'Balkon / parapet',
  orchard: 'Sad / drzewa owocowe',
  herbs: 'Zioła i przyprawy',
  mixed: 'Ogród mieszany',
};

const SOCIAL_META = [
  { key: 'socialInstagram', label: 'Instagram', icon: '📷' },
  { key: 'socialFacebook',  label: 'Facebook',  icon: '📘' },
  { key: 'socialYoutube',   label: 'YouTube',   icon: '▶' },
  { key: 'socialPinterest', label: 'Pinterest', icon: '📌' },
  { key: 'socialWebsite',   label: 'Strona',    icon: '🌐' },
];

function userInitials(displayName, username) {
  const src = displayName || username || '?';
  return src.trim().slice(0, 2).toUpperCase();
}

function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `/${path}`;
}

function seasonNumber(memberSince) {
  if (!memberSince) return 1;
  const yr = new Date(memberSince).getFullYear();
  return Math.max(1, new Date().getFullYear() - yr + 1);
}

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? 'property' : 'name';
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

// ── Featured plant tile ─────────────────────────────────────────────
function FeaturedTile({ featured }) {
  if (!featured) return null;
  const { plantName, plantVariety, isHarvested, daysUntilHarvest, daysSincePlanted, imagePath, plotName, rowNumber, note } = featured;

  let statusLine;
  if (isHarvested) {
    statusLine = 'Ostatnio zebrane w tym sezonie';
  } else if (daysUntilHarvest !== null && daysUntilHarvest > 0) {
    statusLine = `Do zbioru jeszcze ${daysUntilHarvest} ${daysUntilHarvest === 1 ? 'dzień' : 'dni'}`;
  } else if (daysUntilHarvest !== null && daysUntilHarvest <= 0) {
    statusLine = 'Gotowe do zbioru — sezon trwa';
  } else if (daysSincePlanted !== null) {
    statusLine = `W ziemi od ${daysSincePlanted} dni`;
  }

  const cover = imageUrl(imagePath);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: cover ? '1fr 1.4fr' : '1fr',
      gap: 0,
      borderRadius: 16,
      overflow: 'hidden',
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 36,
    }}>
      {cover && (
        <div style={{
          aspectRatio: '4 / 5',
          background: `url(${cover}) center / cover, linear-gradient(135deg, var(--t-sage), var(--t-amber))`,
          minHeight: 240,
        }} />
      )}
      <div style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Meta color="amber" style={{ marginBottom: 10 }}>Roślina sezonu</Meta>
        <Display size={28} style={{
          display: 'block', letterSpacing: '-.025em',
          lineHeight: 1.05, marginBottom: 10,
        }}>{plantName}{plantVariety && <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}> {plantVariety}</span>}</Display>
        {statusLine && (
          <div style={{
            fontSize: 13, color: 'var(--t-text-dim)',
            marginBottom: 12, lineHeight: 1.5,
          }}>{statusLine}</div>
        )}
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.16em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>{plotName} · Rząd {rowNumber}</div>
        {note && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 8,
            background: 'var(--t-bg)',
            fontSize: 12, color: 'var(--t-text-dim)',
            fontStyle: 'italic', lineHeight: 1.45,
          }}>{note}</div>
        )}
      </div>
    </div>
  );
}

// ── Stat card ───────────────────────────────────────────────────────
function StatCard({ label, value, unit, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 100,
      padding: '20px 22px', borderRadius: 14,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      textAlign: 'center',
    }}>
      <Meta style={{ marginBottom: 8 }}>{label}</Meta>
      <Display size={32} style={{
        display: 'block', letterSpacing: '-.025em',
        color: accent || 'var(--t-text)',
        lineHeight: 1,
      }}>{value}</Display>
      {unit && (
        <div style={{
          marginTop: 6, fontSize: 10,
          fontFamily: 'var(--t-mono)', letterSpacing: '.16em',
          textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>{unit}</div>
      )}
    </div>
  );
}

// ── Timeline item ───────────────────────────────────────────────────
function TimelineItem({ item }) {
  const status = item.status || 'growing';
  const statusTone = status === 'ready' ? 'sage' :
                     status === 'soon' ? 'amber' :
                     status === 'harvested' ? 'dim' : 'dim';
  const statusLabel = status === 'ready' ? 'gotowe do zbioru' :
                      status === 'soon' ? 'wkrótce' :
                      status === 'harvested' ? 'zebrane' :
                      status === 'growing' ? 'rośnie' : 'nieokreślone';
  const progress = Math.max(0, Math.min(100, item.progressPercent || 0));

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Display size={16} style={{ display: 'inline', letterSpacing: '-.01em' }}>{item.plantName}</Display>
          {item.plantVariety && (
            <span style={{ color: 'var(--t-text-dim)', fontSize: 13 }}> ({item.plantVariety})</span>
          )}
        </div>
        <Chip tone={statusTone}>{statusLabel}</Chip>
      </div>
      <div style={{
        fontSize: 11, color: 'var(--t-text-mute)',
        fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        marginBottom: 10,
      }}>
        {item.plotName} · Rząd {item.rowNumber}
        {item.daysUntilHarvest != null && item.daysUntilHarvest > 0 && (
          <> · za {item.daysUntilHarvest} dni</>
        )}
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--t-line-2)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: status === 'ready' ? 'var(--t-sage)' :
                      status === 'soon' ? 'var(--t-amber)' : 'var(--t-text-mute)',
          transition: 'width 600ms ease',
        }} />
      </div>
    </div>
  );
}

// ── Harvest row ─────────────────────────────────────────────────────
function HarvestRow({ h }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 16px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: '3px solid var(--t-amber)',
      marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--t-text)', letterSpacing: '-.01em' }}>
          {h.plantName}
          {h.plantVariety && (<span style={{ color: 'var(--t-text-dim)' }}> ({h.plantVariety})</span>)}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--t-text-mute)', marginTop: 2,
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          {h.plotName} · Rząd {h.rowNumber} · {formatDate(h.harvestDate)}
        </div>
        {h.harvestNotes && (
          <div style={{
            fontSize: 12, color: 'var(--t-text-dim)',
            marginTop: 6, lineHeight: 1.45, fontStyle: 'italic',
          }}>{h.harvestNotes}</div>
        )}
      </div>
      {h.yieldAmount && (
        <Display size={18} style={{ color: 'var(--t-amber)', letterSpacing: '-.01em' }}>
          {h.yieldAmount} {h.yieldUnit || 'szt'}
        </Display>
      )}
    </div>
  );
}

// ── Gallery grid ────────────────────────────────────────────────────
function GalleryGrid({ photos }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: 8,
    }}>
      {photos.map(p => (
        <div key={p.id} style={{
          aspectRatio: '1 / 1', borderRadius: 10,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img
            src={imageUrl(p.path)}
            alt={p.caption || ''}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          {p.caption && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '8px 10px',
              background: 'linear-gradient(transparent, rgba(15,20,16,0.9))',
              fontSize: 11, color: 'var(--t-text)',
              lineHeight: 1.3,
            }}>{p.caption}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Mapa Polski (przybliżony pin) ───────────────────────────────────
function PolandPin({ lat, lon }) {
  // Bardzo prosta projekcja: PL ≈ 49°N-55°N, 14°E-24°E → mapa 200×220
  if (lat == null || lon == null) return null;
  const minLat = 49, maxLat = 55;
  const minLon = 14, maxLon = 24;
  const x = ((lon - minLon) / (maxLon - minLon)) * 200;
  const y = ((maxLat - lat) / (maxLat - minLat)) * 220;
  if (x < 0 || x > 200 || y < 0 || y > 220) return null;

  return (
    <svg width="200" height="220" viewBox="0 0 200 220"
      style={{ flexShrink: 0, opacity: 0.9 }}
      aria-label="Przybliżona lokalizacja na mapie Polski">
      {/* Kontur Polski - mocno uproszczony */}
      <path
        d="M40,40 L165,30 L185,55 L185,90 L175,130 L165,160 L150,185 L120,200 L90,205 L60,195 L40,170 L25,140 L20,100 L25,65 Z"
        fill="rgba(168, 196, 158, 0.08)"
        stroke="var(--t-line-2)"
        strokeWidth="1"
      />
      {/* Pin */}
      <circle cx={x} cy={y} r="8" fill="var(--t-amber)" opacity="0.3" />
      <circle cx={x} cy={y} r="4" fill="var(--t-amber)" />
    </svg>
  );
}

// ── Główny komponent ────────────────────────────────────────────────
export default function PublicProfile() {
  const { username } = useParams();
  const { data, loading, error } = useApi(`/api/g/${username}`);
  const [blockMsg, setBlockMsg] = useState('');

  // Update meta tags + title dla SEO/share
  useEffect(() => {
    if (data) {
      const title = `${data.displayName || data.username} · GardenApp.pl`;
      document.title = title;
      const desc = data.tagline || data.bio || `Profil ogrodnika ${data.displayName || data.username} na GardenApp.pl`;
      setMeta('description', desc);
      setMeta('og:title', title, true);
      setMeta('og:description', desc, true);
      setMeta('og:type', 'profile', true);
      setMeta('og:url', `https://gardenapp.pl/g/${data.username}`, true);
      if (data.coverPhoto?.path) {
        const url = imageUrl(data.coverPhoto.path);
        setMeta('og:image', url.startsWith('http') ? url : `https://gardenapp.pl${url}`, true);
      }
    }
    return () => { document.title = 'GardenApp.pl'; };
  }, [data]);

  if (loading) {
    return (
      <div className="redesign-root" style={{
        minHeight: 'var(--t-vh)', background: 'var(--t-bg)',
        color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
      }}>
        <LoadingState />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="redesign-root" style={{
        minHeight: 'var(--t-vh)', background: 'var(--t-bg)',
        color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
        display: 'grid', placeItems: 'center', padding: 32,
      }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <Meta color="amber" style={{ marginBottom: 14 }}>404</Meta>
          <Display size={48} style={{
            display: 'block', letterSpacing: '-.03em', marginBottom: 14,
          }}>Profil nie istnieje</Display>
          <div style={{
            fontSize: 14, color: 'var(--t-text-dim)',
            lineHeight: 1.55, marginBottom: 24,
          }}>
            Profil <code style={{ fontFamily: 'var(--t-mono)' }}>{username}</code> nie istnieje
            albo nie jest publiczny.
          </div>
          <Link to="/" style={{
            display: 'inline-block', padding: '12px 24px', borderRadius: 12,
            background: 'var(--t-sage)', color: 'var(--t-bg)',
            fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
            textDecoration: 'none',
          }}>← Strona główna</Link>
        </div>
      </div>
    );
  }

  const stats = data.stats;
  const timeline = data.timeline;
  const harvests = data.harvests;
  const gallery = data.gallery;
  const featured = data.featured;
  const location = data.location;
  const season = seasonNumber(data.memberSince);
  const cover = imageUrl(data.coverPhoto?.path);
  const socials = SOCIAL_META.filter(s => data[s.key]);

  // Tagline composer: jeśli user ma własny tagline → użyj.
  // Inaczej, gdy zgodzi się na lokalizację → zbuduj z lokalizacji.
  let tagline = data.tagline;
  if (!tagline && location) {
    const parts = [];
    if (location.hardinessZone) parts.push(`Strefa ${location.hardinessZone}`);
    if (location.city) parts.push(location.city);
    if (data.gardenType && GARDEN_TYPE_LABEL[data.gardenType]) {
      parts.push(GARDEN_TYPE_LABEL[data.gardenType]);
    }
    tagline = parts.length > 0 ? parts.join(' · ') : null;
  }

  return (
    <div className="redesign-root" style={{
      minHeight: 'var(--t-vh)',
      background: 'var(--t-bg)',
      color: 'var(--t-text)',
      fontFamily: 'var(--t-sans)',
    }}>
      {/* Cover photo banner */}
      {cover && (
        <div style={{
          height: 280,
          background: `linear-gradient(180deg, rgba(15,20,16,0.2) 0%, rgba(15,20,16,0.95) 100%), url(${cover}) center / cover`,
          marginBottom: -100,
          position: 'relative',
        }} />
      )}

      <div style={{
        maxWidth: 760, margin: '0 auto',
        padding: '40px 32px 60px',
        position: 'relative', zIndex: 2,
      }}>
        {/* Top nav */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 30,
        }}>
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.22em', textTransform: 'uppercase',
            color: cover ? 'var(--t-text)' : 'var(--t-text-mute)',
            textShadow: cover ? '0 1px 4px rgba(0,0,0,.5)' : 'none',
          }}>
            gardenapp.pl/g/{data.username}
          </div>
          <Link to="/" style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-sage)', textDecoration: 'none',
          }}>Strona główna →</Link>
        </div>

        {/* Hero */}
        <div style={{ marginBottom: 36 }}>
          <div style={{
            display: 'flex', gap: 24, alignItems: 'flex-end',
            marginBottom: 24, flexWrap: 'wrap',
          }}>
            {data.profilePhoto ? (
              <img
                src={imageUrl(data.profilePhoto)}
                alt=""
                style={{
                  width: 110, height: 110, borderRadius: '50%',
                  objectFit: 'cover', flexShrink: 0,
                  border: '4px solid var(--t-bg)',
                  boxShadow: cover ? '0 4px 24px rgba(0,0,0,.4)' : 'none',
                }}
              />
            ) : (
              <div style={{
                width: 110, height: 110, borderRadius: '50%',
                background: 'var(--t-sage)', color: 'var(--t-bg)',
                display: 'grid', placeItems: 'center',
                fontFamily: 'var(--t-display)', fontSize: 40, fontWeight: 400,
                flexShrink: 0,
                border: '4px solid var(--t-bg)',
                boxShadow: cover ? '0 4px 24px rgba(0,0,0,.4)' : 'none',
              }}>{userInitials(data.displayName, data.username)}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Meta color="sage" style={{ marginBottom: 6 }}>
                Sezon {season} · Ogrodnik
                {data.memberSince && ` · od ${new Date(data.memberSince).getFullYear()}`}
              </Meta>
              <Display size={'clamp(36px, 6vw, 56px)'} style={{
                display: 'block', letterSpacing: '-.03em', lineHeight: 1,
              }}>{data.displayName}</Display>
              {tagline && (
                <div style={{
                  marginTop: 10,
                  fontSize: 14,
                  fontFamily: 'var(--t-mono)',
                  letterSpacing: '.06em',
                  color: 'var(--t-text-dim)',
                }}>{tagline}</div>
              )}
            </div>
          </div>

          {/* Garden type chip */}
          {data.gardenType && GARDEN_TYPE_LABEL[data.gardenType] && (
            <div style={{ marginBottom: 14 }}>
              <Chip tone="sage">{GARDEN_TYPE_LABEL[data.gardenType]}</Chip>
            </div>
          )}

          {/* Mapa pin (jeśli user wyraził zgodę i ma współrzędne) */}
          {location?.showPin && location.latApprox && location.lonApprox && (
            <div style={{
              display: 'flex', gap: 18, alignItems: 'center',
              padding: '16px 18px', borderRadius: 14,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              marginBottom: 18,
            }}>
              <PolandPin lat={location.latApprox} lon={location.lonApprox} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Meta style={{ marginBottom: 6 }}>Lokalizacja</Meta>
                <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 4 }}>
                  {location.city || 'Polska'}
                </div>
                {location.hardinessZone && (
                  <div style={{
                    fontSize: 11, color: 'var(--t-text-mute)',
                    fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                  }}>strefa mrozoodporności {location.hardinessZone}</div>
                )}
                <div style={{
                  marginTop: 8, fontSize: 10,
                  color: 'var(--t-text-mute)',
                  fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
                  fontStyle: 'italic',
                }}>pin zaokrąglony do ~10 km dla prywatności</div>
              </div>
            </div>
          )}

          {/* Social pills */}
          {socials.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {socials.map(s => (
                <a
                  key={s.key}
                  href={data[s.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', borderRadius: 10,
                    background: 'var(--t-bg-elev)',
                    border: '1px solid var(--t-line)',
                    color: 'var(--t-text-dim)',
                    textDecoration: 'none', fontSize: 13,
                    transition: 'all 200ms ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--t-sage)';
                    e.currentTarget.style.color = 'var(--t-text)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--t-line)';
                    e.currentTarget.style.color = 'var(--t-text-dim)';
                  }}
                >
                  <span aria-hidden="true">{s.icon}</span>
                  {s.label} →
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Block / Report (S2) + Zobacz ogród (S6) — tylko gdy to nie mój profil */}
      {data.username && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18, marginTop: 8,
        }}>
          {blockMsg ? (
            <span style={{ fontSize: 12, color: blockMsg.startsWith('✓') ? 'var(--t-sage)' : 'var(--t-rust)' }}>
              {blockMsg}
            </span>
          ) : (
            <>
              <Link to={`/ogrod-znajomego/${data.id}`} style={{
                padding: '6px 12px', borderRadius: 6, background: 'var(--t-sage)', border: '1px solid var(--t-sage)',
                color: 'var(--t-bg)', fontSize: 11, textDecoration: 'none', cursor: 'pointer', fontFamily: 'var(--t-mono)',
              }}>🌱 Zobacz ogród</Link>
              <button type="button" onClick={async () => {
                try { const ax = await import('../../config/axios'); await ax.default.post(`/api/social/block/${data.id}`); setBlockMsg('✓ Zablokowano'); } catch(e) { setBlockMsg('Błąd'); }
              }} style={{
                padding: '6px 12px', borderRadius: 6, background: 'transparent', border: '1px solid var(--t-rust)',
                color: 'var(--t-rust)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--t-mono)',
              }}>Zablokuj</button>
              <button type="button" onClick={async () => {
                try { const ax = await import('../../config/axios'); await ax.default.post('/api/social/report', { target_type: 'user', target_id: data.id, reason: prompt('Powód zgłoszenia:') }); setBlockMsg('✓ Zgłoszono do moderacji'); } catch(e) { setBlockMsg('Błąd'); }
              }} style={{
                padding: '6px 12px', borderRadius: 6, background: 'transparent', border: '1px solid var(--t-line-2)',
                color: 'var(--t-text-dim)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--t-mono)',
              }}>Zgłoś</button>
            </>
          )}
        </div>
      )}

      {/* Bio (krótkie) */}
        {data.bio && (
          <div style={{
            fontSize: 16, color: 'var(--t-text-dim)',
            lineHeight: 1.6, marginBottom: featured ? 30 : 36,
            paddingLeft: 18,
            borderLeft: '2px solid var(--t-line-2)',
          }}>{data.bio}</div>
        )}

        {/* Featured plant */}
        {featured && <FeaturedTile featured={featured} />}

        {/* Stats */}
        {stats && (
          <>
            <Rule label="I — sezon w liczbach" />
            <div style={{
              display: 'flex', gap: 10, flexWrap: 'wrap',
              marginBottom: 36,
            }}>
              <StatCard label="Działki" value={stats.plots} />
              <StatCard label="Grządki" value={stats.plants} />
              <StatCard label="Plon" value={stats.harvestKg} unit="kg" accent="var(--t-amber)" />
            </div>
          </>
        )}

        {/* Year summary card (current year, gated by public_show_stats) */}
        {data.yearSummary && (
          <div style={{
            padding: '20px 24px', borderRadius: 14,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            marginBottom: 36,
          }}>
            <Meta style={{ marginBottom: 14 }}>Rok {new Date().getFullYear()} w ogrodzie</Meta>
            <div style={{
              display: 'flex', gap: 10, flexWrap: 'wrap',
              marginBottom: data.yearSummary.topPlants?.length ? 14 : 0,
            }}>
              <StatCard
                label="Zbiory"
                value={data.yearSummary.harvestsCount || 0}
                unit="szt."
              />
              <StatCard
                label="Waga"
                value={data.yearSummary.harvestKg?.toFixed(1) || '0.0'}
                unit="kg"
                accent="var(--t-amber)"
              />
              <StatCard
                label="Dni"
                value={data.yearSummary.activeDays || 0}
                unit="aktywne"
              />
            </div>
            {data.yearSummary.topPlants?.length > 0 && (
              <div style={{
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.12em', textTransform: 'uppercase',
                color: 'var(--t-text-mute)',
              }}>
                Top: {data.yearSummary.topPlants.map((p, i) =>
                  `${i + 1}. ${p.plant_name} (${p.count})`
                ).join(' · ')}
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {timeline && timeline.length > 0 && (
          <>
            <Rule label="II — co właśnie rośnie" />
            <div style={{ marginBottom: 36 }}>
              {timeline.slice(0, 6).map(item => (
                <TimelineItem key={item.id} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Harvests */}
        {harvests && harvests.length > 0 && (
          <>
            <Rule label="III — ostatnie zbiory" />
            <div style={{ marginBottom: 36 }}>
              {harvests.map(h => (
                <HarvestRow key={h.id} h={h} />
              ))}
            </div>
          </>
        )}

        {/* Gallery */}
        {gallery && gallery.length > 0 && (
          <>
            <Rule label="IV — galeria" />
            <div style={{ marginBottom: 36 }}>
              <GalleryGrid photos={gallery} />
            </div>
          </>
        )}

        {/* About long */}
        {data.aboutLong && (
          <>
            <Rule label="V — o ogrodzie" />
            <div style={{
              padding: '20px 24px', borderRadius: 14,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              marginBottom: 36,
              fontSize: 15, color: 'var(--t-text-dim)',
              lineHeight: 1.65,
              whiteSpace: 'pre-wrap',
            }}>{data.aboutLong}</div>
          </>
        )}

        {/* Footer */}
        <div style={{
          marginTop: 50, paddingTop: 24,
          borderTop: '1px solid var(--t-line)',
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.16em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)', marginBottom: 12,
          }}>Profil ogrodnika · GardenApp.pl</div>
          <Link to="/register" style={{
            fontFamily: 'var(--t-display)', fontStyle: 'italic',
            fontSize: 14, color: 'var(--t-sage)',
            textDecoration: 'none',
          }}>Stwórz swój własny ogród →</Link>
        </div>
      </div>
    </div>
  );
}
